import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const SHOW_DELAY_MS = 600; // small delay so cursor passing over the handle doesn't immediately flash
const AUTO_DISMISS_MS = 4000;

export type ResizeHintTooltipProps = {
  /** Whether the user has already seen the hint. When true, the component is a no-op. */
  alreadySeen: boolean;
  /** Called once after the hint has been displayed (or skipped because alreadySeen). */
  onShown: () => void;
  /** Whether the AskPuntt route is currently rendering a PanelGroup (i.e., a handle exists). */
  resizableActive: boolean;
};

/**
 * One-time discoverability tooltip for the resize handle. Shows the first
 * time a user lands on AskPuntt with PanelGroup mode active; auto-dismisses
 * after 4s; marks the pref so it never shows again on this browser.
 *
 * Anchored to the body and absolutely-positioned just below the top of the
 * pane area — we don't have a handle ref here so we keep the tooltip
 * floating in the same predictable spot (top center).
 */
export function ResizeHintTooltip({
  alreadySeen,
  onShown,
  resizableActive,
}: ResizeHintTooltipProps) {
  const { t } = useTranslation("ui");
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Stash onShown in a ref so a fresh-closure callback from the parent (an
  // inline arrow re-allocated on every parent render) doesn't restart the
  // entrance timer. Without this, AskPuntt's frequent re-renders during
  // streaming would keep cancelling the 600 ms showTimer and the hint
  // would never paint.
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;

  useEffect(() => {
    if (alreadySeen || dismissed || !resizableActive) return;
    const showTimer = setTimeout(() => {
      setVisible(true);
    }, SHOW_DELAY_MS);
    const hideTimer = setTimeout(() => {
      setVisible(false);
      setDismissed(true);
      // Mark the pref AFTER the full visible window: if we called this in
      // the showTimer instead, React 18 would batch the parent's setState
      // with our setVisible(true) and the next render would have
      // alreadySeen=true AND visible=true, hitting the early-return below
      // before the user ever saw the hint.
      onShownRef.current();
    }, SHOW_DELAY_MS + AUTO_DISMISS_MS);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [alreadySeen, dismissed, resizableActive]);

  if (alreadySeen || dismissed || !visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="askpuntt-resize-hint-tooltip"
      className="pointer-events-none absolute left-1/2 top-2 z-50 -translate-x-1/2 rounded-md bg-puntt-neutral-gray-12 px-3 py-1.5 text-xs font-medium text-base-white shadow-lg"
    >
      {t("resizeHint.instructions")}
    </div>
  );
}
